import React, { useState } from "react";
import { Button, Tag, message, Spin } from "antd";
import {
  ReloadOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useStoresQuery, useUpdateStoreLocationMutation } from "../../Services";
import { PageHeader } from "../../components";
import { Store } from "../../Types";
import { useSectionAccess } from "../../Services";
import { AdminAction } from "../../Types";

export const StoreLiveTracking = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [simulatingLocation, setSimulatingLocation] = useState(false);
  const { isAllowed } = useSectionAccess("admin");

  const { data: storesData, isLoading, refetch } = useStoresQuery({
    page: 1,
    limit: 200,
  });

  const updateLocationMutation = useUpdateStoreLocationMutation();

  const stores: Store[] = Array.isArray(storesData)
    ? storesData
    : (storesData as any)?.stores || [];
  const store = stores.find((s) => String(s.id) === storeId);

  const handleSimulateGps = async () => {
    if (!store) return;
    setSimulatingLocation(true);

    let baseLat = store.latitude ? Number(store.latitude) : 6.9271;
    let baseLng = store.longitude ? Number(store.longitude) : 79.8612;

    const latShift = (Math.random() - 0.5) * 0.003;
    const lngShift = (Math.random() - 0.5) * 0.003;
    const newLat = parseFloat((baseLat + latShift).toFixed(6));
    const newLng = parseFloat((baseLng + lngShift).toFixed(6));

    updateLocationMutation.mutate(
      {
        id: store.id as string,
        latitude: newLat,
        longitude: newLng,
      },
      {
        onSuccess: (res) => {
          setSimulatingLocation(false);
          if (res?.success) {
            message.success("Simulated GPS signal sent successfully!");
            refetch();
          } else {
            message.error(res?.message || "Failed to update location.");
          }
        },
        onError: (err: any) => {
          setSimulatingLocation(false);
          message.error(err?.message || "Failed to update location.");
        },
      }
    );
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50/50">
      <PageHeader
        title={`GPS Tracking - ${store?.name || "Loading..."}`}
        subText={store ? `Live location monitoring for ${store.name}` : ""}
        handleButtons={[
          {
            icon: ArrowLeftOutlined,
            title: "Back to Stores",
            onClick: () => navigate(-1),
          },
          ...(isAllowed(AdminAction.ManageStoreTracking)
            ? [
                {
                  icon: ReloadOutlined,
                  title: "Refresh Map",
                  onClick: () => refetch(),
                },
              ]
            : []),
        ]}
      />

      <Spin spinning={isLoading}>
        {store ? (
          <div className="flex flex-col gap-4">
            {store.latitude && store.longitude ? (
              <div className="flex flex-col gap-4">
                <div className="w-full h-[75vh] overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                  <iframe
                    key={`${store.latitude}-${store.longitude}`}
                    title="Vehicle Map Location"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    src={`https://maps.google.com/maps?q=${store.latitude},${store.longitude}&hl=en&z=15&output=embed`}
                    allowFullScreen
                    className="w-full h-full shadow-inner"
                  />
                </div>
                {isAllowed(AdminAction.ManageStoreTracking) && (
                  <Button
                    type="primary"
                    icon={<EnvironmentOutlined />}
                    onClick={handleSimulateGps}
                    loading={simulatingLocation}
                    className="self-start"
                  >
                    {store.latitude && store.longitude
                      ? "Simulate GPS Movement"
                      : "Initialize GPS Signal"}
                  </Button>
                )}
              </div>
            ) : (
              <div className="py-16 flex flex-col items-center justify-center bg-white rounded-lg border border-dashed border-gray-200 shadow-sm">
                <EnvironmentOutlined className="text-5xl text-gray-300 mb-3" />
                <p className="font-semibold text-gray-700 m-0">
                  No GPS Signal Found
                </p>
                <p className="text-xs text-gray-400 max-w-[320px] text-center mt-1 mb-4">
                  This vehicle has not reported any GPS coordinates yet. Click
                  initialize to send a sample GPS signal.
                </p>
                {isAllowed(AdminAction.ManageStoreTracking) && (
                  <Button
                    type="primary"
                    icon={<EnvironmentOutlined />}
                    onClick={handleSimulateGps}
                    loading={simulatingLocation}
                  >
                    Initialize GPS Signal
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="py-16 flex flex-col items-center justify-center bg-white rounded-lg border border-dashed border-gray-200">
            <EnvironmentOutlined className="text-5xl text-gray-300 mb-3" />
            <p className="font-semibold text-gray-700 m-0">Store Not Found</p>
            <p className="text-xs text-gray-400 max-w-[320px] text-center mt-1">
              The requested store could not be found or loaded.
            </p>
            <Button
              type="primary"
              onClick={() => navigate(-1)}
              className="mt-4"
            >
              Go Back
            </Button>
          </div>
        )}
      </Spin>
    </div>
  );
};
